import { createSelector } from "reselect";
import { isOrderableGroup, isQuotableGroup, isBundleQuoteGroup, type LineItem } from "../../models/CartData";
import type { OrderableGroup, QuotableGroup, BundleQuoteGroup } from "../../dto/CartResponseDTO";
import * as WeightUtils from "../../../../../utils/WeightUtils";
import * as PriceUtils from "../../../../../utils/PriceUtils";
import { type RootState } from "~/app/store";
import { isKITKITProduct, type ProductData } from "~/domains/product/types/ProductData";
import {
  type DeliveryModeData,
  type FreightPaymentsData,
  type SupportedPaymentsTypesData,
} from "../../transformers/CartResponseTransformer";
import { getTotalPriceForLineItem } from "~/features/cart/cart/utils/lineItemPriceUtils";

export const selectOrderableGroup = createSelector(
  (state: RootState) => state.cart.entryGroups,
  (entryGroups): OrderableGroup | null => (entryGroups.find(isOrderableGroup) as OrderableGroup) ?? null,
);

export const selectQuotableGroup = createSelector(
  (state: RootState) => state.cart.entryGroups,
  (entryGroups): QuotableGroup | null => (entryGroups.find(isQuotableGroup) as QuotableGroup) ?? null,
);

export const selectBundleGroups = createSelector(
  (state: RootState) => state.cart.entryGroups,
  (entryGroups): BundleQuoteGroup[] => entryGroups.filter(isBundleQuoteGroup) as BundleQuoteGroup[],
);

export const selectTotalWeight = createSelector(
  (state: RootState) => state.cart.packageEntriesById,
  (packageEntriesById) => {
    const packEntries = Object.values(packageEntriesById);
    return WeightUtils.sum(...packEntries.map((entry) => WeightUtils.multiply(entry.weight, entry.quantity)));
  },
);

export const selectAvailableDeliveryModes = (state: RootState, paymentType: string) => {
  return state.checkoutPage.supportedPaymentTypes.find((item) => item.code === paymentType)?.deliveryModes ?? [];
};
export const selectDeliveryModeDetails =
  (deliveryCode: string, paymentType: string) =>
  (state: RootState): DeliveryModeData | undefined =>
    selectAvailableDeliveryModes(state, paymentType).find((item) => item.code === deliveryCode);
export const selectDeliveryModes =
  (paymentType: string) =>
  (state: RootState): SupportedPaymentsTypesData | undefined =>
    state.checkoutPage.supportedPaymentTypes.find((item) => item.code === paymentType);

export const selectSelectedUnloadingPoint = (state: RootState) => state.checkoutPage.unloadingPoint;
export const selectIsSending = (state: RootState) => state.checkoutPage.sending;
export const selectInitialDeliveryMode = (state: RootState) =>
  state.checkoutPage?.supportedPaymentTypes[0]?.deliveryModes[0]?.code ?? "";
export const selectInitialFreightPayment = (state: RootState) =>
  state.checkoutPage?.supportedPaymentTypes[0]?.deliveryModes[0]?.freightPayments
    ? state.checkoutPage?.supportedPaymentTypes[0]?.deliveryModes[0]?.freightPayments[0]?.id
    : "";

export const selectFreightPayment =
  (deliveryCode: string, paymentType: string) =>
  (state: RootState): DeliveryModeData | undefined =>
    selectAvailableDeliveryModes(state, paymentType).find((item) => item.code === deliveryCode);
export const selectAvailableFreightPayments = (state: RootState, deliveryCode: string, paymentType: string) => {
  return (
    selectAvailableDeliveryModes(state, paymentType).find((item) => item.code === deliveryCode)?.freightPayments ?? []
  );
};
export const selectFreightPaymentDetails =
  (deliveryCode: string, paymentType: string, freightPayment: string) =>
  (state: RootState): FreightPaymentsData | undefined =>
    selectAvailableFreightPayments(state, deliveryCode, paymentType).find((item) => item.id === freightPayment);

const isLineItemValidForCalculatingTotalValue = (products: Dict<ProductData>) => (lineItem: LineItem) => {
  const product = products[lineItem.productCode];
  return !isKITKITProduct(product);
};

export const selectTotalCheckoutValue = createSelector(
  (state: RootState) => state.cart.packageEntriesById,
  (state: RootState) => state.products.basic,
  selectOrderableGroup,
  selectBundleGroups,
  (packageEntriesById, products, orderableGroup, bundleGroups) => {
    const checkoutRootNumbers = new Set<number>();
    if (orderableGroup) {
      checkoutRootNumbers.add(orderableGroup.groupNumber);
    }
    for (const bundle of bundleGroups) {
      checkoutRootNumbers.add(bundle.groupNumber);
    }

    const lineItems = Object.values(packageEntriesById)
      .filter((entry) => checkoutRootNumbers.has(entry.groupNumbers[0]))
      .filter(isLineItemValidForCalculatingTotalValue(products));
    return PriceUtils.volatileSum(
      ...lineItems.map((entry) => {
        const product: ProductData | null = products[entry.productCode];
        return getTotalPriceForLineItem(entry, product);
      }),
    );
  },
);

// TODO: step-15 — migrate to tree-based FCC filter
export const selectSalesmanCheckoutCart = selectOrderableGroup;
